import { FormEvent, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Lock, Mountain } from 'lucide-react'

import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { resetPassword } from '../lib/api/auth'

export const ResetPasswordPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = useMemo(() => searchParams.get('token') ?? '', [searchParams])
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const validate = () => {
    if (!token) {
      return 'Password reset link is invalid or expired.'
    }

    if (newPassword.length < 6) {
      return 'Password must be at least 6 characters.'
    }

    if (newPassword !== confirmPassword) {
      return 'Passwords do not match.'
    }

    return null
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setMessage(null)
    setError(null)

    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    setLoading(true)
    try {
      const response = await resetPassword(token, newPassword)
      setMessage(response.message)
      setNewPassword('')
      setConfirmPassword('')
      window.setTimeout(() => navigate('/login', { replace: true }), 1200)
    } catch (err: unknown) {
      console.error(err)
      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as { response?: { data?: { message?: string } } }).response
        setError(response?.data?.message ?? 'Unable to reset password.')
      } else {
        setError('Unable to reset password.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background topo-pattern p-8">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-lg">
            <Mountain className="h-6 w-6" strokeWidth={2.5} />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-accent">HikeStrong</p>
            <h2 className="text-xl font-bold tracking-tight text-foreground">Base Camp</h2>
          </div>
        </div>

        <div className="elevation-card rounded-2xl p-8">
          <div className="mb-8">
            <div className="mb-3 flex items-center gap-2">
              <div className="h-1 w-8 rounded-full bg-accent"></div>
              <span className="font-mono text-xs font-bold uppercase tracking-wider text-accent">
                Account Recovery
              </span>
            </div>
            <h1 className="text-3xl font-bold text-foreground">Choose New Password</h1>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Set a new password for your dashboard account.
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="reset-password" className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
                <Lock className="h-4 w-4 text-muted-foreground" />
                New Password
              </label>
              <Input
                id="reset-password"
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                className="h-12"
                required
                minLength={6}
              />
            </div>

            <div>
              <label htmlFor="reset-confirm-password" className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
                <Lock className="h-4 w-4 text-muted-foreground" />
                Confirm Password
              </label>
              <Input
                id="reset-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="h-12"
                required
                minLength={6}
              />
            </div>

            {message && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-sm font-medium text-emerald-700">{message}</p>
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-sm font-medium text-red-700">{error}</p>
              </div>
            )}

            <Button type="submit" className="h-12 w-full text-base" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset password'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm font-semibold text-primary hover:underline">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
